import { cn } from '../../lib/utils'
import { Sparkles } from 'lucide-react'

interface JournalLine {
  account_code: string
  account_name: string
  debit: number
  credit: number
}

interface AiInsightCardProps {
  title: string
  confidence: number
  description: string
  journalEntry?: JournalLine[]
  citation?: string
  onApply?: () => void
  onModify?: () => void
  onDismiss?: () => void
  className?: string
}

export function AiInsightCard({
  title,
  confidence,
  description,
  journalEntry,
  citation,
  onApply,
  onModify,
  onDismiss,
  className,
}: AiInsightCardProps) {
  const confidenceColor =
    confidence >= 80 ? 'bg-emerald-500' :
    confidence >= 60 ? 'bg-amber-500' : 'bg-red-500'

  return (
    <div className={cn(
      'rounded-lg border border-white/[0.06] bg-[var(--bg-elevated,var(--bg-elevated))]',
      'border-l-[3px] border-l-[var(--accent,var(--accent))] p-4',
      className,
    )}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[var(--accent,var(--accent))]" />
        <span className="text-xs font-semibold text-[var(--accent,var(--accent))] uppercase tracking-wider">
          AI Resolution
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all', confidenceColor)}
              style={{ width: `${confidence}%` }}
            />
          </div>
          <span className="text-[10px] font-mono text-[var(--text-muted)]">
            {confidence}%
          </span>
        </div>
      </div>

      {/* Title + Description */}
      <h4 className="text-sm font-medium text-[var(--text-primary,#F0F2F5)] mb-1">
        {title}
      </h4>
      <p className="text-xs text-[var(--text-secondary,#94A3B8)] mb-3 leading-relaxed">
        {description}
      </p>

      {/* Journal Entry Preview */}
      {journalEntry && journalEntry.length > 0 && (
        <div className="rounded-md border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-2 mb-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase text-[var(--text-muted)]">
                <th className="text-left pb-1">Account</th>
                <th className="text-right pb-1">DR</th>
                <th className="text-right pb-1">CR</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {journalEntry.map((line, i) => (
                <tr key={i}>
                  <td className="py-0.5 text-[var(--text-secondary)]">
                    {line.account_code} {line.account_name}
                  </td>
                  <td className="text-right py-0.5 text-[var(--text-primary)]">
                    {line.debit > 0 ? `$${line.debit.toLocaleString()}` : ''}
                  </td>
                  <td className="text-right py-0.5 text-[var(--text-primary)]">
                    {line.credit > 0 ? `$${line.credit.toLocaleString()}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Citation */}
      {citation && (
        <p className="text-[10px] italic text-[var(--text-hint,#475569)] mb-3">
          {citation}
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        {onApply && (
          <button
            onClick={onApply}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-medium',
              'bg-[var(--accent,var(--accent))] text-[var(--bg-base,var(--bg-base))]',
              'hover:opacity-90 transition-opacity',
            )}
          >
            Apply
          </button>
        )}
        {onModify && (
          <button
            onClick={onModify}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-medium',
              'border border-white/[0.1] text-[var(--text-primary)]',
              'hover:bg-white/[0.06] transition-colors',
            )}
          >
            Modify
          </button>
        )}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs',
              'text-[var(--text-muted)] hover:text-[var(--text-primary)]',
              'transition-colors',
            )}
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  )
}
